import React, { useEffect, useState } from "react"

import { useNamespacedTranslation } from "../hooks/useNamespacedTranslation"

import { LocationOn } from "@mui/icons-material"
import { IconButton, CircularProgress, Typography } from "@mui/material"
import { LocationService } from "./LocationService"
import { StatButton } from "react-ui-components/components/StatButton"

export const CountryCityStateFetcher = ({ label }: any) => {
  const [loading, setLoading] = useState(false)
  const { t } = useNamespacedTranslation(['common'])
  const [showButton, setShowButton] = useState(true)

  useEffect(() => {
    try {
      const hasGeoApi = typeof navigator !== "undefined" && !!navigator.geolocation
      const userRegion =
        typeof navigator !== "undefined" && (Intl as any).Locale
          ? new Intl.Locale(navigator.language).region
          : null

      if (hasGeoApi || userRegion) {
        setShowButton(true)
      } else {
        setShowButton(false)
      }
    } catch (error: any) {
      setShowButton(false)
      // Geolocation error - fail silently
    }
  }, [])

  if (!showButton) {
    return null
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={() => {
          handleGetLocation({ setLoading })
        }}
        className="flex flex-row gap-1"
        sx={{
          borderRadius: 2,
          px: 0.75,
          py: 0.5,
          color: (theme) => (theme.palette.mode === "dark" ? "#d8f5e6" : "#6b7280"),
          "& .MuiSvgIcon-root": {
            color: "inherit",
          },
        }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : <LocationOn />}
        {!label ? null : (
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              fontWeight: 500,
              fontSize: "1rem",
              lineHeight: 1,
              color: "inherit",
            }}
          >
            {t(label)}
          </Typography>
        )}
      </IconButton>
    </>
  )
}

export const MyLocationIcon: React.FC<{
  label?: string
  loading?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}> = ({ label, loading, onClick }) => {
  const { t } = useNamespacedTranslation(['common'])

  return (
    <StatButton
      link
      icon={loading ? <CircularProgress size={20} color="inherit" /> : <LocationOn />}
      label={!label ? "" : t(label)}
      onClick={onClick as any}
    />
  )
  // return (
  //   <>
  //     <IconButton size="small" color="secondary" onClick={onClick} className="flex flex-row gap-1">
  //       {loading ? <CircularProgress size={20} color="inherit" /> : <LocationOn />}
  //       {!label ? null : t(label)}
  //     </IconButton>
  //   </>
  // )
}
/*
if (!showButton) {
    return PopupService.pushPopup(<UserLocationSelector />)
  }
*/
const canRequestGeolocation = async () => {
  if (typeof navigator === "undefined") return false
  if (!("permissions" in navigator) || !navigator.permissions?.query) {
    return true
  }

  try {
    const status = await navigator.permissions.query({ name: "geolocation" as PermissionName })
    return status.state !== "denied"
  } catch {
    return true
  }
}

export const handleGetLocation = async (props?: { setData?: any; setLoading?: any; local?: boolean }) => {
  if (props?.local) {
    const localLocStr = localStorage.getItem("WINLOC")
    if (localLocStr) {
      try {
        const savedLOC = JSON.parse(localLocStr)
        if (savedLOC) {
          LocationService.setLocation(savedLOC)
          return
        }
      } catch (err) {
        console.warn("Invalid cached location found in WINLOC; ignoring", err)
      }
    }
  }

  props?.setLoading?.(true)

  const geoAvailable = typeof navigator !== "undefined" && !!navigator.geolocation
  if (!geoAvailable) {
    console.warn("Geolocation is not supported by this browser.")
    props?.setLoading?.(false)
    return
  }

  const allowed = await canRequestGeolocation()
  if (!allowed) {
    console.info("Geolocation permission denied; skipping location request.")
    props?.setLoading?.(false)
    return
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const { latitude, longitude } = position.coords
      fetchLocationData(latitude, longitude)
      props?.setLoading?.(false)
    },
    (error) => {
      if (error?.code === error?.PERMISSION_DENIED) {
        console.info("User denied geolocation request.")
      } else {
        console.warn("Error getting geolocation:", error)
      }
      props?.setLoading?.(false)
    },
  )
}

// Exported separately from fetchLocationData so the parsing logic can be unit
// tested without mocking the browser geolocation API / fetch.
export const extractLocationFromAddressComponents = (addressComponents: any[]) => {
  let country = ""
  let state = ""
  let city = ""
  let zip = ""

  ;(addressComponents || []).forEach((component: any) => {
    const types = component.types
    if (types.includes("country")) {
      country = component.short_name
    } else if (types.includes("administrative_area_level_1")) {
      state = component.long_name
    } else if (types.includes("locality")) {
      city = component.long_name
    } else if (types.includes("postal_code")) {
      zip = component.long_name || component.short_name
    }
  })

  return { country, state, city, zip }
}

const fetchLocationData = async (latitude: number, longitude: number) => {
  try {
    const apiKey = process.env.REACT_APP_GOOGLE_GEO_API_KEY
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`,
    )

    const responseData = await response.json()

    if (responseData.status === "OK") {
      const addressComponents = responseData.results?.[0]?.address_components || []
      const { country, state, city, zip } = extractLocationFromAddressComponents(addressComponents)

      LocationService.setLocation({
        country: country,
        state: state,
        city: city,
        zip: zip,
      })

      // Optionally, save the data to Firestore
      // await addDoc(collection(db, 'locations'), { country, city, state });
    } else {
      // fetchLocationData is a plain utility invoked fire-and-forget from the browser
      // geolocation success callback (see handleGetLocation below) - nothing awaits its
      // returned promise, so there is no caller that could observe a thrown/rejected error.
      console.error("Failed to retrieve location data:", responseData.status)
    }
  } catch (error) {
    console.error("Error fetching location data:", error)
  }
}

export const getCurrentLocation: {} = () => {}

export default CountryCityStateFetcher
